<template>
  <v-autocomplete
    data-testid="tagsSelector"
    clearable
    dense
    item-text="text"
    item-value="situation"
    multiple
    outlined
    small-chips
    hide-details
    :disabled="disabled"
    :items="availableSituationTypes"
    :label="$t('settings.situation_types')"
    :loading="isLoading"
    :value="inputSelection"
    :search-input.sync="searchTerm"
    @change="handleChange"
  >
    <template v-slot:selection="{ item, index }">
      <v-chip
        v-if="index < maxChipsShown"
        class="mt-2 mb-1"
        outlined
        small
        :ripple="false"
      >
        <span v-if="index < lastChip">
          {{ item.text }}
        </span>

        <span v-if="index === lastChip">
          {{
            $t('common.plusOthersWithCount_other', {
              count: value.length - lastChip,
            })
          }}
        </span>
      </v-chip>
    </template>
  </v-autocomplete>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

import api from '@/api'

export default {
  name: 'SituationTypesSelector',

  props: {
    // Despite consumers will pass an array of objects we need to parse it as an array of situation strings before passing it as prop for the autocomplete component. Check the computed prop "inputSelection".
    value: {
      type: Array,
      default: () => [],
    },
    disabled: Boolean,
    presetSituationTypes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      availableSituationTypes: [],
      isLoading: false,
      maxChipsShown: 4,
      searchTerm: '',
      situationTypesMap: {},
    }
  },

  computed: {
    ...mapGetters(['organizationId', 'siteId']),

    inputSelection() {
      return this.value?.map?.(situationType => situationType.situation)
    },

    lastChip() {
      return this.maxChipsShown - 1
    },
  },

  created() {
    this.fetchAvailableSituationTypes()
  },

  methods: {
    async fetchSituationTypes() {
      if (!_.isEmpty(this.presetSituationTypes)) {
        return this.presetSituationTypes
      } else {
        return await api.situationType.getSiteSituationTypesList(
          this.organizationId,
          this.siteId
        )
      }
    },

    async fetchAvailableSituationTypes() {
      this.isLoading = true
      const response = await this.fetchSituationTypes()

      const responseData = !_.isEmpty(this.presetSituationTypes)
        ? response
        : response.data
      this.availableSituationTypes = this.buildSituationTypesGroups(
        responseData
      )
      // Will be used when emitting changes. Check the "handleChange" method.
      this.situationTypesMap = _.keyBy(responseData, 'situation')

      this.isLoading = false
    },

    buildSituationTypesGroups(situationTypes) {
      const groups = _.groupBy(situationTypes, 'type')
      const orderedTypeKeys = Object.keys(groups).sort()

      return orderedTypeKeys.reduce((result, type) => {
        const itemsInType = groups[type]
        if (_.isEmpty(itemsInType)) return result

        const orderedItems = this.getOrderedSituationTypeList(groups[type])

        return [
          ...result,
          { header: this.$t(`quick_action.situation_types.${type}`) },
          { divider: true },
          ...orderedItems,
        ]
      }, [])
    },

    getOrderedSituationTypeList(situationTypes) {
      return _.chain(situationTypes)
        .map(item => ({
          ...item,
          text: this.$t(
            `quick_action.situation_types.situations.${item.situation}`
          ),
        }))
        .orderBy('text')
        .value()
    },

    handleChange(selectedItems) {
      this.searchTerm = ''
      // The emitted changes by v-autocomplete only include the "situation" string attribute (["situation_1", "situation_2"]). We need to emit full situation type objects.
      this.$emit(
        'input',
        selectedItems.map(item => this.situationTypesMap[item])
      )
    },

    handleRemove(situationType) {
      const index = this.value?.findIndex(
        item => item.situation === situationType.situation
      )

      if (index === -1) return

      const newSelection = [...this.value]
      newSelection.splice(index, 1)

      this.$emit('input', newSelection)
    },
  },
}
</script>
